/**
 * Reader readability preferences (Task 189, decision 5) — a small reusable hook
 * persisting reading comfort settings to localStorage so they survive across
 * sessions. Applied by the reader as CSS vars / classes. Frontend-only; no
 * server state.
 */
import { useCallback, useEffect, useState } from "react";

export const READER_SCALES = [0.92, 1, 1.12, 1.28, 1.45] as const;
export type ReaderType = "sans" | "serif";

export interface ReaderPrefs {
  scaleIndex: number; // index into READER_SCALES
  type: ReaderType;
}

const KEY = "camino:reader:prefs:v1";
const DEFAULT: ReaderPrefs = { scaleIndex: 1, type: "serif" };

function load(): ReaderPrefs {
  if (typeof window === "undefined") return DEFAULT;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Partial<ReaderPrefs>;
    const scaleIndex =
      typeof parsed.scaleIndex === "number" && parsed.scaleIndex >= 0 && parsed.scaleIndex < READER_SCALES.length
        ? parsed.scaleIndex
        : DEFAULT.scaleIndex;
    const type: ReaderType = parsed.type === "sans" || parsed.type === "serif" ? parsed.type : DEFAULT.type;
    return { scaleIndex, type };
  } catch {
    return DEFAULT;
  }
}

export interface ReaderPrefsApi {
  prefs: ReaderPrefs;
  scale: number;
  larger: () => void;
  smaller: () => void;
  toggleType: () => void;
  canLarger: boolean;
  canSmaller: boolean;
}

export function useReaderPrefs(): ReaderPrefsApi {
  const [prefs, setPrefs] = useState<ReaderPrefs>(load);

  useEffect(() => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(prefs));
    } catch {
      /* storage unavailable — non-fatal */
    }
  }, [prefs]);

  const larger = useCallback(
    () => setPrefs((p) => ({ ...p, scaleIndex: Math.min(READER_SCALES.length - 1, p.scaleIndex + 1) })),
    [],
  );
  const smaller = useCallback(() => setPrefs((p) => ({ ...p, scaleIndex: Math.max(0, p.scaleIndex - 1) })), []);
  const toggleType = useCallback(() => setPrefs((p) => ({ ...p, type: p.type === "serif" ? "sans" : "serif" })), []);

  return {
    prefs,
    scale: READER_SCALES[prefs.scaleIndex] ?? 1,
    larger,
    smaller,
    toggleType,
    canLarger: prefs.scaleIndex < READER_SCALES.length - 1,
    canSmaller: prefs.scaleIndex > 0,
  };
}
